// ===========================================
// Needs Checking Dialog (#642 Phase 2 Request 7)
// ===========================================
// The owner's flag on a payment that may not match the bank ("Principal for now"): a reason from
// Laura's list and an optional note ("Other" needs one), or, on an open flag, a resolution note.
// The server's refusal is shown as it comes.

import { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import type { Payment, PaymentCheckReason } from '../../api/invoices.api';
import { useFlagPaymentNeedsChecking, useResolvePaymentCheck } from '../../hooks/useInvoices';
import { apiErrorMessage } from '../../utils/apiError';
import { formatCurrencyFromDollars } from '../../utils/currency';
import { PAYMENT_CHECK_REASON_LABELS } from '../../utils/invoiceDisplay';

interface NeedsCheckingDialogProps {
  open: boolean;
  onClose: () => void;
  /** 'set' flags the payment; 'resolve' closes its open flag. */
  mode: 'set' | 'resolve';
  payment: Payment | null;
}

const MIN_NOTE = 3;
const REASONS = Object.keys(PAYMENT_CHECK_REASON_LABELS) as PaymentCheckReason[];

export default function NeedsCheckingDialog({ open, onClose, mode, payment }: NeedsCheckingDialogProps) {
  const [reason, setReason] = useState<PaymentCheckReason>('NOT_IN_BANK');
  const [note, setNote] = useState('');
  const flag = useFlagPaymentNeedsChecking();
  const resolve = useResolvePaymentCheck();
  const mutation = mode === 'set' ? flag : resolve;

  const close = () => {
    setReason('NOT_IN_BANK');
    setNote('');
    flag.reset();
    resolve.reset();
    onClose();
  };

  const noteRequired = mode === 'resolve' || reason === 'OTHER';
  const valid = !noteRequired || note.trim().length >= MIN_NOTE;

  const confirm = async () => {
    if (!payment || !valid) return;
    try {
      if (mode === 'set') {
        await flag.mutateAsync({ paymentId: payment.id, reason, note: note.trim() || null });
      } else {
        await resolve.mutateAsync({ paymentId: payment.id, note: note.trim(), flaggedAt: payment.needsCheckingAt });
      }
      close();
    } catch {
      // Shown below from mutation.error.
    }
  };

  const amount = payment ? formatCurrencyFromDollars(payment.amount) : '';

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle>{mode === 'set' ? 'Mark as needs checking' : 'Resolve the check'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {mode === 'set'
            ? `Flag this ${amount} payment to check against the bank statement.`
            : `Say how the ${amount} payment was checked. The flag clears; the history is kept.`}
        </Typography>
        {mode === 'set' && (
          <TextField
            select
            fullWidth
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value as PaymentCheckReason)}
            sx={{ mb: 2 }}
          >
            {REASONS.map((r) => (
              <MenuItem key={r} value={r}>
                {PAYMENT_CHECK_REASON_LABELS[r]}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          fullWidth
          multiline
          minRows={2}
          label={mode === 'resolve' ? 'How it was resolved' : reason === 'OTHER' ? 'What needs checking' : 'Note (optional)'}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: 500 }}
          required={noteRequired}
        />
        {mutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {apiErrorMessage(mutation.error, 'Could not save. Please try again.')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          onClick={confirm}
          disabled={!valid || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {mode === 'set' ? 'Mark as needs checking' : 'Resolve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
